// Plausibility limits: how long a real sleep, feed or pump can last. Parents forget timers and come back after
// days or weeks, so the log must never turn a forgotten timer into "fed for 88 hours".
// - A running timer past its limit was forgotten: the app asks when it really ended (it is not stopped "now").
// - Stats never count a feed beyond MAX_FEED_MIN or a finished sleep longer than MAX_FINISHED_SLEEP_MIN.
// - "Awake for" longer than MAX_AWAKE_MIN means sleeps were not logged, not that the baby was awake.
import { MAX_ONGOING_SLEEP_MIN } from './sleep-sessions.js';
import { localMinutesOfDay } from './tz.js';

/** Longest a running timer can plausibly run, minutes. */
export const MAX_RUNNING_MIN: Record<string, number> = {
  sleep: MAX_ONGOING_SLEEP_MIN,
  breast: 120,
  bottle: 90,
  pump: 90,
  custom: 24 * 60,
};

/** A usual length, minutes: the quick fix for a forgotten timer ("it ended about 20 min later"). */
export const TYPICAL_MIN: Record<string, number> = {
  sleep: 60,
  breast: 20,
  bottle: 15,
  pump: 20,
  custom: 30,
};

/** A finished sleep longer than this is a timer stopped days late: stats leave it out. */
export const MAX_FINISHED_SLEEP_MIN = 24 * 60;
/** One feed never counts for more than this in the stats (breastfeeding minutes). */
export const MAX_FEED_MIN = 180;
/** Awake longer than this = sleeps were not logged. */
export const MAX_AWAKE_MIN = 12 * 60;
/** No entries for this long = the parent was away: the app welcomes them back instead of showing stale numbers. */
export const AWAY_AFTER_MIN = 24 * 60;

const ms = (t: string | number) => (typeof t === 'number' ? t : Date.parse(t));

/** A running timer of this type started at `startAt` is past any real length. */
export function runningTooLong(type: string, startAt: string | number, now: number): boolean {
  const max = MAX_RUNNING_MIN[type];
  return max !== undefined && now - ms(startAt) > max * 60_000;
}

type Seg = { side: string; startAt: string; endAt: string | null };

/**
 * The end the app gives a forgotten timer when it closes it itself, never more than MAX_RUNNING_MIN after the start
 * (the server refuses longer entries, and a refused fix would bounce back forever):
 * a breastfeed ends where its last real side ended (a side "resumed" days later is dropped); a sleep that started
 * in the evening or at night is a night (10.5 h), any other sleep a nap (1 h); the rest a usual length.
 */
export function autoEndAt(e: { type: string; startAt: string; data?: { segments?: Seg[] } }, now: number, tz: string): number {
  const start = Date.parse(e.startAt);
  const cap = Math.min(now, start + (MAX_RUNNING_MIN[e.type] ?? 24 * 60) * 60_000);
  const segs = e.type === 'breast' ? (e.data?.segments ?? []).filter((s) => Date.parse(s.startAt) < cap) : [];
  let end: number;
  if (segs.length && segs.every((s) => s.endAt)) end = Math.max(start, ...segs.map((s) => Date.parse(s.endAt!)));
  else if (segs.length) {
    const open = segs.find((s) => !s.endAt)!;
    const closedEnd = Math.max(start, ...segs.filter((s) => s.endAt).map((s) => Date.parse(s.endAt!)));
    end = Math.max(closedEnd, Date.parse(open.startAt) + 15 * 60_000);
  } else if (e.type === 'sleep') {
    const m = localMinutesOfDay(start, tz);
    end = start + (m >= 18 * 60 || m < 5 * 60 ? 10.5 * 60 : 60) * 60_000;
  } else end = suggestedEnd(e.type, start, now);
  return Math.max(start, Math.min(end, cap));
}

/** Close a forgotten timer: the end from autoEndAt, breastfeed sides cut to it (sides after it dropped), and the
 *  autoEnded flag so the parent can check the guess. Same rule on the phone (autoClose.ts) and in scripts. */
export function closeForgotten<D extends Record<string, unknown>>(e: { type: string; startAt: string; data: D }, now: number, tz: string): { endAt: string; data: D & { autoEnded: { at: string } } } {
  const end = autoEndAt(e as { type: string; startAt: string; data?: { segments?: Seg[] } }, now, tz);
  const data: Record<string, unknown> = { ...e.data, autoEnded: { at: new Date(now).toISOString() } };
  if (e.type === 'breast' && Array.isArray((e.data as { segments?: Seg[] }).segments)) {
    data.segments = ((e.data as unknown as { segments: Seg[] }).segments)
      .filter((x) => Date.parse(x.startAt) < end)
      .map((x) => ({ ...x, endAt: new Date(Math.min(end, x.endAt ? Date.parse(x.endAt) : end)).toISOString() }));
  }
  return { endAt: new Date(end).toISOString(), data: data as D & { autoEnded: { at: string } } };
}

/** The end to suggest for a forgotten timer: a usual length after the start, never in the future. */
export function suggestedEnd(type: string, startAt: string | number, now: number): number {
  return Math.min(now, ms(startAt) + (TYPICAL_MIN[type] ?? 30) * 60_000);
}
